/**
 * ExamApplicationModal Component
 * Modal for setting exam application details (periodicity, dates, etc.)
 *
 * @version 2025.1.0
 */

import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  ApplicationData,
  AvailableExam,
  ExamWithApplication,
  isExamWithApplication,
} from '../../types/examApplication';
import { parseFrequencyToMonths } from '../../utils/examFrequency';

// ============================================================================
// TYPES
// ============================================================================

export interface ExamApplicationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when form is submitted */
  onConfirm: (data: ApplicationData) => Promise<void>;
  /** The exam being applied/edited */
  exam: AvailableExam | ExamWithApplication | null;
  /** Mode: 'apply' for new application, 'edit' for existing */
  mode: 'apply' | 'edit';
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
}

interface FormValues {
  periodicityMonths: number | null;
  lastExamDate: Date | null;
  nextExamDate: Date | null;
  isRequired: boolean;
  observations: string;
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

const validationSchema: yup.ObjectSchema<FormValues> = yup.object({
  periodicityMonths: yup
    .number()
    .nullable()
    .defined()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value
    )
    .min(1, 'Periodicidade deve ser pelo menos 1 mês')
    .max(120, 'Periodicidade não pode exceder 120 meses (10 anos)'),
  lastExamDate: yup.date().nullable().defined(),
  nextExamDate: yup
    .date()
    .nullable()
    .defined()
    .when('lastExamDate', {
      is: (val: Date | null) => val !== null,
      then: (schema) =>
        schema.min(
          yup.ref('lastExamDate'),
          'Data do próximo exame deve ser posterior à data do último exame'
        ),
    }),
  isRequired: yup.boolean().required().defined(),
  observations: yup.string().defined().max(500, 'Observações não podem exceder 500 caracteres'),
});

// ============================================================================
// COMPONENT
// ============================================================================

export const ExamApplicationModal: React.FC<ExamApplicationModalProps> = ({
  open,
  onClose,
  onConfirm,
  exam,
  mode,
  loading = false,
  error = null,
}) => {
  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      periodicityMonths: null,
      lastExamDate: null,
      nextExamDate: null,
      isRequired: true,
      observations: '',
    },
  });

  // Watch for auto-calculation
  const periodicityMonths = watch('periodicityMonths');
  const lastExamDate = watch('lastExamDate');

  // Reset form when modal opens/closes or exam changes
  useEffect(() => {
    if (open && exam) {
      if (isExamWithApplication(exam)) {
        // Editing existing application
        reset({
          periodicityMonths: exam.periodicityMonths ?? null,
          lastExamDate: exam.lastExamDate ? new Date(exam.lastExamDate) : null,
          nextExamDate: exam.nextExamDate ? new Date(exam.nextExamDate) : null,
          isRequired: exam.isRequired,
          observations: exam.observations ?? '',
        });
      } else {
        // New application — pré-preenche a periodicidade a partir da
        // frequência padrão do catálogo do exame (#266), editável pelo usuário.
        reset({
          periodicityMonths: parseFrequencyToMonths(exam.defaultFrequency),
          lastExamDate: null,
          nextExamDate: null,
          isRequired: true,
          observations: '',
        });
      }
    }
  }, [open, exam, reset]);

  // Auto-calculate next exam date
  useEffect(() => {
    if (lastExamDate && periodicityMonths && periodicityMonths > 0) {
      const nextDate = new Date(lastExamDate);
      nextDate.setMonth(nextDate.getMonth() + periodicityMonths);
      setValue('nextExamDate', nextDate);
    }
  }, [lastExamDate, periodicityMonths, setValue]);

  // Submit handler
  const onSubmit = async (data: FormValues) => {
    const applicationData: ApplicationData = {
      periodicityMonths: data.periodicityMonths,
      lastExamDate: data.lastExamDate?.toISOString().split('T')[0] ?? null,
      nextExamDate: data.nextExamDate?.toISOString().split('T')[0] ?? null,
      isRequired: data.isRequired,
      observations: data.observations ?? null,
    };

    await onConfirm(applicationData);
  };

  // Get exam name for display
  const examName = exam?.examName ?? 'Exame';

  return (
    <Dialog
      open={open}
      onClose={loading || isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {mode === 'apply' ? 'Aplicar Exame' : 'Editar Aplicação'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {examName}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Stack spacing={3}>
              {error && (
                <Alert severity="error" onClose={() => {}}>
                  {error}
                </Alert>
              )}

              {/* Required toggle */}
              <Controller
                name="isRequired"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        disabled={loading || isSubmitting}
                      />
                    }
                    label="Exame obrigatório"
                  />
                )}
              />

              {/* Periodicity */}
              <Controller
                name="periodicityMonths"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? null : parseInt(value, 10));
                    }}
                    label="Periodicidade"
                    type="number"
                    fullWidth
                    error={!!errors.periodicityMonths}
                    helperText={
                      errors.periodicityMonths?.message ||
                      'Intervalo em meses entre exames'
                    }
                    disabled={loading || isSubmitting}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">meses</InputAdornment>
                      ),
                      inputProps: { min: 1, max: 120 },
                    }}
                  />
                )}
              />

              {/* Last Exam Date */}
              <Controller
                name="lastExamDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="Data do último exame"
                    disabled={loading || isSubmitting}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.lastExamDate,
                        helperText: errors.lastExamDate?.message,
                      },
                    }}
                  />
                )}
              />

              {/* Next Exam Date */}
              <Controller
                name="nextExamDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="Data do próximo exame"
                    disabled={loading || isSubmitting}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.nextExamDate,
                        helperText:
                          errors.nextExamDate?.message ||
                          (lastExamDate && periodicityMonths
                            ? 'Calculado automaticamente com base na periodicidade'
                            : undefined),
                      },
                    }}
                  />
                )}
              />

              {/* Observations */}
              <Controller
                name="observations"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Observações"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.observations}
                    helperText={
                      errors.observations?.message ||
                      `${field.value?.length ?? 0}/500 caracteres`
                    }
                    disabled={loading || isSubmitting}
                  />
                )}
              />

              {/* Info box */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'info.50',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'info.200',
                }}
              >
                <Typography variant="body2" color="info.800">
                  <strong>Dica:</strong> Preencha a periodicidade e a data do
                  último exame para calcular automaticamente a próxima data.
                </Typography>
              </Box>
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading || isSubmitting}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || isSubmitting}
            startIcon={
              (loading || isSubmitting) && <CircularProgress size={16} />
            }
          >
            {mode === 'apply' ? 'Aplicar' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ExamApplicationModal;
